import { useState, useRef, useCallback } from 'react'
import axios, { CancelTokenSource } from 'axios'

export const formatBytes = (bytes: number, decimals: number = 2) => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

interface ISignedUploadData {
  file: File
  id: string
  name?: string
  signedUrl: string
}

export const useUpload = () => {
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [uploads, setUploads] = useState<any>({})
  const cancelSourcesRef = useRef<CancelTokenSource[]>([])
  const createdFileIdsRef = useRef<string[]>([])
  const cancelledRef = useRef(false)

  const cancel = useCallback(() => {
    cancelledRef.current = true
    cancelSourcesRef.current.forEach((source) => source.cancel('Upload cancelled by user'))
    cancelSourcesRef.current = []
  }, [])

  const upload = async (fileUploads: ISignedUploadData[]) => {
    setLoading(true)
    cancelledRef.current = false
    createdFileIdsRef.current = fileUploads.map((f) => f.id)
    cancelSourcesRef.current = []

    try {
      const result = await Promise.all(
        fileUploads.map(async (f) => {
          const source = axios.CancelToken.source()
          cancelSourcesRef.current.push(source)

          setUploads((u: any) => ({
            ...u,
            [f.signedUrl]: {
              name: f.name,
              id: f.id,
              progress: 0,
              loaded: '0 Bytes',
              total: formatBytes(f.file.size),
            },
          }))

          await axios.put(f.signedUrl, f.file, {
            headers: { 'Content-Type': f.file.type || 'application/octet-stream' },
            cancelToken: source.token,
            onUploadProgress: (progressEvent) => {
              setUploads((u: any) => ({
                ...u,
                [f.signedUrl]: {
                  ...u[f.signedUrl],
                  progress: Math.round((progressEvent.loaded / progressEvent.total) * 100),
                  loaded: formatBytes(progressEvent.loaded),
                  total: formatBytes(f.file.size),
                },
              }))
            },
          })

          return f.id
        }),
      )

      setLoading(false)
      setProgress(0)
      setUploads({})
      cancelSourcesRef.current = []
      createdFileIdsRef.current = []

      return result
    } catch (err) {
      // Cancel all remaining uploads
      cancelSourcesRef.current.forEach((s) => s.cancel('Cleanup'))
      cancelSourcesRef.current = []

      setLoading(false)
      setProgress(0)
      setUploads({})

      if (cancelledRef.current || axios.isCancel(err)) {
        const idsToClean = [...createdFileIdsRef.current]
        createdFileIdsRef.current = []
        cancelledRef.current = false
        return { cancelled: true, fileIds: idsToClean }
      }

      throw err
    }
  }

  return [upload, { progress, loading, uploads, cancel }] as const
}

export default useUpload
